import React from "react";
import { useTranslation } from "react-i18next";
import {
  useAppSelector,
  useAppDispatch,
  useMediaQuery,
} from "../../../../../common/hooks/hooks";
import { setPage } from "../../productSlice";

const DOTS = "...";

/**
 * A hook to generate the pagination range with ellipsis.
 * @param currentPage - The current active page.
 * @param totalPages - The total number of pages.
 * @param siblingCount - How many page numbers to show on each side of the current page.
 * @returns An array of page numbers and ellipsis strings.
 */
const usePaginationRange = (
  currentPage: number,
  totalPages: number,
  siblingCount: number = 1
): (number | string)[] => {
  return React.useMemo(() => {
    // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS
    const totalPageNumbers = siblingCount + 5;

    // Case 1: If the number of pages is less than the page numbers we want to show
    if (totalPageNumbers >= totalPages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;

    // Case 2: No left dots to show, but right dots needed
    if (!shouldShowLeftDots && shouldShowRightDots) {
      let leftItemCount = 3 + 2 * siblingCount;
      let leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
      return [...leftRange, DOTS, totalPages];
    }

    // Case 3: No right dots to show, but left dots needed
    if (shouldShowLeftDots && !shouldShowRightDots) {
      let rightItemCount = 3 + 2 * siblingCount;
      let rightRange = Array.from(
        { length: rightItemCount },
        (_, i) => totalPages - rightItemCount + i + 1
      );
      return [firstPageIndex, DOTS, ...rightRange];
    }

    // Case 4: Both left and right dots needed
    if (shouldShowLeftDots && shouldShowRightDots) {
      let middleRange = Array.from(
        { length: rightSiblingIndex - leftSiblingIndex + 1 },
        (_, i) => leftSiblingIndex + i
      );
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
    }

    return []; // Fallback
  }, [currentPage, totalPages, siblingCount]);
};

// Define the component's props to accept the fetching state
interface ProductsPaginationProps {
  isFetching: boolean;
}

const ProductsPagination: React.FC<ProductsPaginationProps> = ({
  isFetching,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { currentPage, totalPages } = useAppSelector(
    (state) => state.product.pagination
  );

  // Use a media query to determine if the screen is small.
  // Below 768px, we'll show fewer page numbers.
  const isSmallScreen = useMediaQuery("(max-width: 768px)");

  // Adjust the number of sibling pages shown based on screen size.
  // 0 siblings for small screens (e.g., [1, ..., 5, ..., 10])
  // 1 sibling for larger screens (e.g., [1, ..., 4, 5, 6, ..., 10])
  const siblingCount = isSmallScreen ? 0 : 1;

  const paginationRange = usePaginationRange(
    currentPage,
    totalPages,
    siblingCount
  );

  const onPageChange = (page: number) => {
    // Prevent changing page while a fetch is already in progress
    if (page !== currentPage && !isFetching) {
      dispatch(setPage(page));
      // UX Improvement: scroll to top of product grid after page change
      document
        .querySelector(".products-archive__container")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const onNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const onPrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  // If there's only one page (or if data is still loading where totalPages is 1),
  // there's no need to render the pagination component. This elegantly handles
  // the initial loading, error, and single-page states without visual clutter.
  if (totalPages <= 1) {
    return null;
  }

  // When products data is available and totalPages > 1, render the pagination controls
  return (
    <nav
      className="products-pagination"
      aria-label={t("productsArchive.pagination.navLabel")}
    >
      {/* Add a class to the list when fetching to provide visual feedback */}
      <ul
        className={`products-pagination__list ${
          isFetching ? "is-fetching" : ""
        }`}
      >
        {/* Previous Button */}
        <li className="products-pagination__item">
          <button
            className="products-pagination__button"
            onClick={onPrevious}
            disabled={currentPage === 1 || isFetching}
            aria-label={t("productsArchive.pagination.prevLabel")}
          >
            &laquo;
          </button>
        </li>

        {/* Page Number Buttons */}
        {paginationRange.map((pageNumber, index) => {
          if (pageNumber === DOTS) {
            return (
              <li
                key={`${DOTS}-${index}`}
                className="products-pagination__item products-pagination__ellipsis"
              >
                &#8230;
              </li>
            );
          }
          const isCurrent = pageNumber === currentPage;
          return (
            <li key={pageNumber} className="products-pagination__item">
              <button
                className={`products-pagination__button ${
                  isCurrent ? "is-active" : ""
                }`}
                onClick={() => onPageChange(pageNumber as number)}
                disabled={isFetching}
                aria-label={t(
                  isCurrent
                    ? "productsArchive.pagination.currentPageLabel"
                    : "productsArchive.pagination.pageLabel",
                  { pageNumber }
                )}
                aria-current={isCurrent ? "page" : undefined}
              >
                {pageNumber}
              </button>
            </li>
          );
        })}

        {/* Next Button */}
        <li className="products-pagination__item">
          <button
            className="products-pagination__button"
            onClick={onNext}
            disabled={currentPage === totalPages || isFetching}
            aria-label={t("productsArchive.pagination.nextLabel")}
          >
            &raquo;
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default ProductsPagination;
